import type { PromptJob, ShotItem } from './store';
import { referenceMergeContext, type RpcApi } from './referenceVideo';

/** Saved native handles own the merge, not the current engine picker. */
export function isNativeMergeShot(shot: ShotItem): boolean {
  if (shot.fkSceneId || shot.fkVideoRequestId || shot.rpcSubmissionPending) return true;
  // A later completed Antigravity run can retain an older RPC engine stamp.
  // Active native handles still take precedence above; a stamp alone cannot.
  return shot.engine !== 'antigravity' && (shot.engine === 'flowkit' || !!shot.rpcRequestEngine);
}

const pending = new Map<string, Promise<string>>();

export async function mergeReferenceShots(api: RpcApi, job: PromptJob, shots: ShotItem[]): Promise<string> {
  const ordered = [...shots].sort((left, right) => left.shotNumber - right.shotNumber);
  if (!ordered.length || ordered.some(shot => shot.jobId !== job.id || shot.status !== 'done')) {
    throw new Error('Every selected shot must be finished and belong to the same job. No clips were omitted.');
  }
  if (ordered.some(shot => !isNativeMergeShot(shot))) {
    throw new Error('Legacy board clips must be merged in their original Flowboard board. Select native scene-backed clips separately; no partial merge was submitted.');
  }
  const sceneIds = ordered.map(shot => shot.fkSceneId);
  if (sceneIds.some(id => typeof id !== 'string' || !/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(id))) {
    throw new Error('A finished shot has no saved scene handle. No clips were omitted; keep the existing videos and inspect that shot.');
  }
  if (new Set(sceneIds).size !== sceneIds.length) throw new Error('Two shots reference the same scene. No clips were silently duplicated.');
  const context = referenceMergeContext(job, ordered);
  const body = {
    ...context,
    sceneIds,
    orientation: ['16:9', '4:3'].includes(job.aspect) ? 'HORIZONTAL' : 'VERTICAL',
    jobId: job.id,
    ...(job.studioJobId ? { studioJobId: job.studioJobId } : {}),
  };
  const key = JSON.stringify(body);
  const previous = pending.get(key);
  if (previous) return previous;
  const request = (async () => {
    // Merge existing video/audio only. Narration is a separate explicit action,
    // not a hidden paid prerequisite or something to retry before every merge.
    const { data } = await api.post('/flowkit-gen/merge', body, { timeout: 300_000 });
    if (typeof data?.mergedUrl !== 'string' || !/^\/flowkit-media\/[a-f0-9]{64}\.mp4$/.test(data.mergedUrl)
      || data.clipCount !== ordered.length) {
      throw new Error('The merge response did not confirm every selected clip and a playable output. The saved shots were not changed.');
    }
    return data.mergedUrl;
  })();
  pending.set(key, request);
  try { return await request; } finally { pending.delete(key); }
}